import type { PreviewInput } from "@/api/commands";
import { ENGINE_PROVIDER } from "@/lib/engine";
import type { Backtest, BacktestResult } from "@/api/types";
import { alignStoredBacktestConfig } from "@/lib/strategy-leverage";
import { effectiveBarMagnifier } from "@/lib/bar-magnifier";
import { timeframeMinutes } from "@/lib/new-backtest";
import { ensurePinnedStrategyConfig, hasPinnedStrategySource } from "@/lib/script-save";

/** Just over the server's 600s preview timeout so the backend error arrives first. */
export const PREVIEW_TIMEOUT_MS = 610_000;

export function toDateOnly(value?: string | null): string {
  if (!value) return "";
  const s = String(value).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m?.[1] || (s.length >= 10 ? s.slice(0, 10) : s);
}

/** HH:mm from a stored start/end value; defaults to 00:00 when date-only. */
export function toTimeOnly(value?: string | null): string {
  if (!value) return "";
  const s = String(value).trim();
  const m = s.match(/[T ](\d{2}):(\d{2})/);
  if (m) return `${m[1]}:${m[2]}`;
  return toDateOnly(s) ? "00:00" : "";
}

/** Canonical UTC wall time for API/CLI: `YYYY-MM-DD HH:MM:00`. */
export function joinDateTime(date: string, time?: string | null): string {
  const d = toDateOnly(date);
  if (!d) return "";
  const t = (time && /^\d{2}:\d{2}/.test(time) ? time.slice(0, 5) : "00:00");
  return `${d} ${t}:00`;
}

/** Normalize any accepted start/end string to `YYYY-MM-DD HH:MM:00` (UTC). */
export function toDateTime(value?: string | null): string {
  if (!value) return "";
  const date = toDateOnly(value);
  if (!date) return "";
  return joinDateTime(date, toTimeOnly(value) || "00:00");
}

/** Current UTC calendar date (`YYYY-MM-DD`). */
export function nowUtcDate(): string {
  const t = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** Start of the current UTC day: `YYYY-MM-DD 00:00:00`. */
export function nowUtcDayStart(): string {
  return joinDateTime(nowUtcDate(), "00:00");
}

/** Current UTC time truncated to the minute: `YYYY-MM-DD HH:MM:00`. */
export function nowUtcDateTime(): string {
  const t = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${nowUtcDate()} ${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}:00`;
}

/** Today at 00:00 UTC, the default start of a new live strategy. */
export function todayUtcStart(): string {
  return `${nowUtcDate()} 00:00:00`;
}

export function hasEndDate(bt: { end_date?: string | null }): boolean {
  return toDateOnly(bt.end_date).length === 10;
}

/** Persistent loops ignore end_date; only open-ended runs should stay enabled. */
export function persistLiveEnabled(endDate?: string | null): boolean {
  return !hasEndDate({ end_date: endDate });
}

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

/** Open time ("YYYY-MM-DD HH:MM:00", UTC) of the newest chart bar that has already closed; "" for an unknown timeframe. */
export function lastClosedBarOpen(timeframe: string | undefined, now = Date.now()): string {
  const mins = timeframeMinutes(timeframe || "");
  if (!mins) return "";
  const tfMs = mins * 60_000;
  return new Date(Math.floor(now / tfMs) * tfMs - tfMs).toISOString().slice(0, 16).replace("T", " ") + ":00";
}

export function buildPreviewPayload(bt: {
  market_provider?: string;
  market_config?: Record<string, unknown>;
  symbol?: string;
  timeframe?: string;
  strategy_provider?: string;
  strategy_config?: Record<string, unknown>;
  engine_provider?: string;
  engine_config?: Record<string, unknown>;
  start_date?: string;
  end_date?: string | null;
  /** Bar Magnifier timeframe (lib/bar-magnifier.ts). */
  bar_magnifier?: string | null;
  /** Recalculation flags the script declares (lib/recalculate.ts). */
  recalculate?: number;
  metadata?: Record<string, unknown> | null;
  scripts?: { id: string; rev_no?: number }[];
}): PreviewInput {
  const aligned = alignStoredBacktestConfig(bt);
  const strategy_config = ensurePinnedStrategyConfig(aligned.strategy_config, {
    metadata: bt.metadata,
    scripts: bt.scripts,
    strategyProvider: bt.strategy_provider,
  });
  const payload: PreviewInput = {
    market_provider: bt.market_provider || "",
    market_config: bt.market_config || {},
    symbol: bt.symbol || "",
    timeframe: bt.timeframe || "",
    strategy_provider: bt.strategy_provider || "pinescript",
    strategy_config,
    engine_provider: ENGINE_PROVIDER,
    engine_config: aligned.engine_config,
    start_date: toDateTime(bt.start_date),
  };
  if (hasEndDate(bt)) payload.end_date = toDateTime(bt.end_date);
  if (bt.recalculate) payload.recalculate = bt.recalculate;
  const magnifier = effectiveBarMagnifier(bt.timeframe, bt.bar_magnifier);
  if (magnifier) {
    payload.bar_magnifier = magnifier;
    // An open-ended magnified run waits on the exchange's live feed until the
    // bar still forming closes (up to a whole chart bar) instead of finishing:
    // end it at the last bar that has closed.
    if (!payload.end_date) {
      const end = lastClosedBarOpen(bt.timeframe);
      if (end) payload.end_date = end;
    }
  }
  return payload;
}

/**
 * Payload for `backtest.preview_stream`. Same shape as preview but no
 * end_date (the stream always continues into live after historical replay)
 * and no Bar Magnifier.
 */
export function buildPreviewStreamPayload(bt: {
  market_provider?: string;
  market_config?: Record<string, unknown>;
  symbol?: string;
  timeframe?: string;
  strategy_provider?: string;
  strategy_config?: Record<string, unknown>;
  engine_provider?: string;
  engine_config?: Record<string, unknown>;
  start_date?: string;
  /** Bar Magnifier timeframe (lib/bar-magnifier.ts). */
  bar_magnifier?: string | null;
  /** Recalculation flags the script declares (lib/recalculate.ts). */
  recalculate?: number;
  metadata?: Record<string, unknown> | null;
  scripts?: { id: string; rev_no?: number }[];
}): PreviewInput {
  const aligned = alignStoredBacktestConfig(bt);
  const strategy_config = ensurePinnedStrategyConfig(aligned.strategy_config, {
    metadata: bt.metadata,
    scripts: bt.scripts,
    strategyProvider: bt.strategy_provider,
  });
  return {
    market_provider: bt.market_provider || "",
    market_config: bt.market_config || {},
    symbol: bt.symbol || "",
    timeframe: bt.timeframe || "",
    strategy_provider: bt.strategy_provider || "pinescript",
    strategy_config,
    engine_provider: ENGINE_PROVIDER,
    engine_config: aligned.engine_config,
    start_date: toDateTime(bt.start_date),
    ...(bt.recalculate ? { recalculate: bt.recalculate } : {}),
    // No bar_magnifier here: a magnified stream holds its history back until the
    // bar still forming closes. The chart's candles don't need it, and its trade
    // markers are taken from the magnified run's result instead.
  };
}

function checksum(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return `${s.length}:${h}`;
}

/** Same value → same JSON, whatever the key order (the server returns Go maps unordered). */
function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === "object") {
    return Object.fromEntries(
      Object.keys(v as Record<string, unknown>)
        .sort()
        .map((k) => [k, sortKeys((v as Record<string, unknown>)[k])])
    );
  }
  return v;
}

export function previewSignature(bt: {
  start_date?: string;
  end_date?: string | null;
  timeframe?: string;
  symbol?: string;
  market_provider?: string;
  market_config?: Record<string, unknown>;
  engine_provider?: string;
  strategy_config?: Record<string, unknown>;
  engine_config?: Record<string, unknown>;
  recalculate?: number;
  bar_magnifier?: string | null;
}): string {
  const aligned = alignStoredBacktestConfig(bt);
  const inputs = { ...aligned.strategy_config };
  const source = String(inputs.source || "");
  delete inputs.source;
  return JSON.stringify({
    start: toDateTime(bt.start_date),
    end: toDateTime(bt.end_date),
    tf: bt.timeframe,
    symbol: bt.symbol || "",
    market: bt.market_provider,
    market_config: sortKeys(bt.market_config || {}),
    engine: ENGINE_PROVIDER,
    engine_config: sortKeys(aligned.engine_config),
    inputs: sortKeys(inputs),
    source: checksum(source),
    ...(bt.recalculate ? { recalculate: bt.recalculate } : {}),
    ...(effectiveBarMagnifier(bt.timeframe, bt.bar_magnifier) ? { bar_magnifier: effectiveBarMagnifier(bt.timeframe, bt.bar_magnifier) } : {}),
  });
}

export function buildPreviewSyncUpdate(bt: {
  id: string;
  start_date?: string;
  end_date?: string | null;
  market_provider?: string;
  market_config?: Record<string, unknown>;
  symbol?: string;
  timeframe?: string;
  strategy_provider?: string;
  strategy_config?: Record<string, unknown>;
  engine_provider?: string;
  engine_config?: Record<string, unknown>;
  /** Bar Magnifier timeframe (lib/bar-magnifier.ts). */
  bar_magnifier?: string | null;
  /** Recalculation flags the script declares (lib/recalculate.ts). */
  recalculate?: number;
  metadata?: Record<string, unknown> | null;
  scripts?: { id: string; rev_no?: number }[];
}): Partial<Backtest> & { id: string } {
  const aligned = alignStoredBacktestConfig(bt);
  const strategy_config = ensurePinnedStrategyConfig(aligned.strategy_config, {
    metadata: bt.metadata,
    scripts: bt.scripts,
    strategyProvider: bt.strategy_provider,
  });
  // Never persist a pinescript update that still has no source after healing.
  // Do NOT write engine_config here — Settings → Properties owns it. Align/seed
  // on every Run was clobbering initial / pyramiding / qty after a Settings save.
  const update: Partial<Backtest> & { id: string } = {
    id: bt.id,
    market_provider: bt.market_provider || "",
    market_config: bt.market_config || {},
    symbol: bt.symbol || "",
    timeframe: bt.timeframe || "",
    strategy_provider: bt.strategy_provider || "pinescript",
    start_date: toDateTime(bt.start_date),
    ...(hasEndDate(bt) ? { end_date: toDateTime(bt.end_date) } : {}),
    enabled: false,
  };
  if (hasPinnedStrategySource(strategy_config) || bt.strategy_provider !== "pinescript") {
    update.strategy_config = strategy_config;
  }
  // Sent whenever it was resolved, 0 included: a script that dropped the flag clears it.
  if (bt.recalculate !== undefined) update.recalculate = bt.recalculate;
  // A backtest saved with the removed "spot" engine: move it to the one engine
  // the backend has, with its capital carried over (the one case engine_config is written here).
  if (bt.engine_provider && bt.engine_provider !== ENGINE_PROVIDER) {
    update.engine_provider = ENGINE_PROVIDER;
    update.engine_config = aligned.engine_config;
  }
  return update;
}

export function readCachedPreview(bt: { id: string } & Parameters<typeof previewSignature>[0]): BacktestResult | null {
  try {
    const raw = sessionStorage.getItem(`lab-preview:${bt.id}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.sig !== previewSignature(bt)) return null;
    return parsed.result ?? null;
  } catch {
    return null;
  }
}

export function writeCachedPreview(
  bt: { id: string } & Parameters<typeof previewSignature>[0],
  result: BacktestResult
) {
  try {
    sessionStorage.setItem(`lab-preview:${bt.id}`, JSON.stringify({ sig: previewSignature(bt), result }));
  } catch {
    /* quota */
  }
}

export function backtestProgressCopy(
  bt: { timeframe?: string; start_date?: string; end_date?: string | null },
  opts: { previewing?: boolean; hasResult?: boolean }
): string {
  const start = toDateTime(bt.start_date) || "the start date";
  const end = toDateTime(bt.end_date);
  const tf = bt.timeframe || "candles";
  if (opts.previewing || (hasEndDate(bt) && !opts.hasResult)) {
    const range = end ? `from ${start} to ${end}` : `from ${start}`;
    return `Replaying ${tf} ${range}. Smaller timeframes take longer to run.`;
  }
  if (opts.hasResult && !hasEndDate(bt)) {
    return "History complete. Following live candles until you stop the backtest.";
  }
  return `Replaying ${tf} from ${start}. This can take several minutes.`;
}
